import { useState, useEffect } from "react";
import { StoreLayout } from "../../components/StoreLayout";
import { useCart } from "../../context/CartContext";
import { useLocationCtx } from "../../context/LocationContext";
import { api, apiError, fmtINR } from "../../lib/api";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { loc } = useLocationCtx();
  const navigate = useNavigate();
  const [form, setForm] = useState({ address: "", scheduled_date: "", slot: "", notes: "" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slotCfg, setSlotCfg] = useState({ slots: [], min_date: "", max_date: "", availability: {} });

  useEffect(() => {
    const params = {};
    if (loc?.city) params.city = loc.city;
    if (form.scheduled_date) params.date = form.scheduled_date;
    api.get("/public/booking-slots", { params }).then((r) => {
      setSlotCfg(r.data);
      setForm((f) => ({ ...f, scheduled_date: f.scheduled_date || r.data.min_date }));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc?.city, form.scheduled_date]);

  const itemUpfront = (s) => {
    const mode = s.payment_mode || "full";
    if (mode === "after") return 0;
    if (mode === "partial") return Math.min(s.booking_amount || 0, s.amount);
    return s.amount;
  };
  const upfrontTotal = items.reduce((sum, s) => sum + itemUpfront(s), 0);
  const balanceTotal = Math.max(0, total - upfrontTotal);

  const submit = async () => {
    if (!form.address.trim()) { toast.error("Please enter your service address"); return; }
    if (!loc?.pincode) { toast.error("Please set your location (pincode) using the location selector in the header"); return; }
    if (!agree) { toast.error("Please accept the Terms & Conditions to continue"); return; }
    setLoading(true);
    try {
      const { data: order } = await api.post("/payments/customer/create-order", { items, ...form, pincode: loc.pincode, accept_terms: true });
      if (order.mode === "after") {
        clear();
        navigate("/booking-confirmed", { state: { count: order.count, total: order.total } });
        return;
      }
      const pay = await openRazorpayCheckout(order, { description: "Fixigo service booking" });
      const { data } = await api.post("/payments/customer/verify", {
        order_ref: order.order_ref,
        razorpay_order_id: pay.razorpay_order_id,
        razorpay_payment_id: pay.razorpay_payment_id,
        razorpay_signature: pay.razorpay_signature,
      });
      clear();
      navigate("/booking-confirmed", { state: { count: data.count, total: data.total } });
    } catch (e) {
      if (e?.message === "Payment cancelled") toast.error("Payment cancelled");
      else toast.error(apiError(e.response?.data?.detail) || e.message);
    }
    finally { setLoading(false); }
  };

  if (items.length === 0) {
    return (
      <StoreLayout>
        <div className="max-w-2xl mx-auto px-5 py-16 text-center">
          <p className="text-muted-foreground">Your cart is empty.</p>
          <Button className="rounded-sm mt-4" onClick={() => navigate("/services")}>Browse Services</Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <div className="max-w-5xl mx-auto px-5 py-12 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-3 space-y-5" data-testid="checkout-form">
          <h1 className="font-heading text-3xl font-bold tracking-tight">Checkout</h1>
          <div>
            <Label className="label-caps">Service Address</Label>
            <Textarea data-testid="checkout-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="House/flat, street, area, city, pincode" className="rounded-sm mt-1.5" />
          </div>
          <div className={`rounded-sm p-3 text-sm flex items-center justify-between ${loc?.pincode ? "bg-green-50 border border-green-200 text-green-800" : "bg-amber-50 border border-amber-200 text-amber-800"}`} data-testid="checkout-location">
            {loc?.pincode
              ? <span>📍 Serviceable location: <strong>{loc.pincode}</strong>{loc.city ? ` · ${loc.city}` : ""}</span>
              : <span>⚠️ No location set. Use the location selector in the header to check serviceability.</span>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="label-caps">Preferred Date</Label>
              <Input data-testid="checkout-date" type="date" value={form.scheduled_date} min={slotCfg.min_date} max={slotCfg.max_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="rounded-sm mt-1.5" />
              {slotCfg.min_date && <p className="text-[11px] text-muted-foreground mt-1">Earliest available: {slotCfg.min_date}</p>}
            </div>
            <div>
              <Label className="label-caps">Time Slot</Label>
              <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="checkout-slot"><SelectValue placeholder="Select slot" /></SelectTrigger>
                <SelectContent>{(slotCfg.slots || []).map((s) => {
                  const av = (slotCfg.availability || {})[s];
                  const full = av?.full;
                  return <SelectItem key={s} value={s} disabled={full}>{s}{full ? " · Full" : (av?.remaining != null ? ` · ${av.remaining} left` : "")}</SelectItem>;
                })}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="label-caps">Notes (optional)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1.5" placeholder="Anything the professional should know?" />
          </div>
          <div className={`rounded-sm p-4 text-sm ${upfrontTotal <= 0 ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-blue-50 border border-blue-200 text-blue-800"}`}>
            {upfrontTotal <= 0
              ? <>💡 <strong>Pay after service</strong> — no online payment now. You'll pay {fmtINR(total)} once the job(s) are completed.</>
              : balanceTotal > 0
              ? <>🔒 Pay <strong>{fmtINR(upfrontTotal)}</strong> now via Razorpay; the balance <strong>{fmtINR(balanceTotal)}</strong> is payable after the service.</>
              : <>🔒 <strong>Secure online payment</strong> — pay the full amount now via Razorpay. Your booking is confirmed instantly after payment.</>}
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="bg-card border border-border rounded-sm p-5 sticky top-20" data-testid="order-summary">
            <h3 className="font-heading font-semibold mb-3">Order Summary</h3>
            <div className="space-y-2 text-sm">
              {items.map((s, i) => (
                <div key={i} className="flex justify-between">
                  <span className="text-muted-foreground">{s.service_type}
                    {(s.payment_mode === "after") && <span className="ml-1 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">pay after</span>}
                    {(s.payment_mode === "partial") && <span className="ml-1 text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">₹{s.booking_amount} now</span>}
                  </span>
                  <span className="font-mono">{fmtINR(s.amount)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-border mt-3 pt-3 flex justify-between font-heading font-bold">
              <span>Total</span><span data-testid="summary-total">{fmtINR(total)}</span>
            </div>
            {upfrontTotal !== total && (
              <div className="mt-2 space-y-1 text-sm" data-testid="summary-split">
                <div className="flex justify-between text-green-700"><span>Pay now</span><span className="font-mono font-semibold">{fmtINR(upfrontTotal)}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Due after service</span><span className="font-mono">{fmtINR(balanceTotal)}</span></div>
              </div>
            )}
            <label className="flex items-start gap-2 cursor-pointer select-none mt-4" data-testid="checkout-terms-label">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border accent-primary" data-testid="checkout-terms-checkbox" />
              <span className="text-xs text-muted-foreground leading-relaxed">
                I agree to the{" "}
                <a href="/customer-terms" target="_blank" rel="noreferrer" className="text-primary font-semibold hover:underline" data-testid="checkout-terms-link">Customer Terms &amp; Conditions</a>, including the cancellation policy.
              </span>
            </label>
            <Button className="rounded-sm w-full mt-5 h-11" onClick={submit} disabled={loading || !agree} data-testid="confirm-booking">
              {loading ? "Processing…" : (upfrontTotal <= 0 ? "Confirm Booking" : `Pay ${fmtINR(upfrontTotal)} & Book`)}
            </Button>
          </div>
        </div>
      </div>
    </StoreLayout>
  );
}
